/**
 * ConsistencySelfTestCard — super-admin diagnostic panel.
 *
 * One click runs `GET /api/self-test/consistency` (read-only, ~100 ms
 * on preview) which probes six drift patterns across users, accounts
 * and positions. Renders the anomaly list grouped by severity and
 * exposes a "Purge orphans" one-click action for the low-severity
 * cruft that accumulates from historical seed churn.
 *
 * Feb 26 2026 — added after the same class of bug bit twice in one
 * week (Kenya-only phone validator, admin-unfreeze not resetting
 * drawdown baselines). The self-test flags this style of drift the
 * moment it appears.
 */
import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
    ShieldCheck, RefreshCw, Loader2, Trash2, AlertTriangle, CheckCircle2,
} from "lucide-react";
import { useSortableData } from "@/hooks/useSortableData";
import { SortableTh } from "@/components/ui/sortable-th";

function SeverityPill({ level, count }) {
    const cls = {
        high:   "text-danger border-danger/40 bg-danger/10",
        medium: "text-amber-600 border-amber-500/40 bg-amber-500/10",
        low:    "text-muted-foreground border-border bg-secondary/50",
    }[level];
    return (
        <span
            data-testid={`selftest-sev-${level}`}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded border text-xs font-bold uppercase tracking-wide ${cls}`}
        >
            {count} {level}
        </span>
    );
}

export default function ConsistencySelfTestCard() {
    const [scanning, setScanning] = useState(false);
    const [report, setReport] = useState(null);
    const [purging, setPurging] = useState(false);
    const [confirmPurge, setConfirmPurge] = useState(false);

    async function runScan() {
        setScanning(true);
        try {
            const { data } = await api.get("/self-test/consistency");
            setReport(data);
            const { high, medium, low } = data.by_severity || {};
            if (high || medium) {
                toast.warning(`${high} high · ${medium} medium anomalies — review below`);
            } else {
                toast.success(`Clean scan · ${low || 0} low-severity notes`);
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || "Scan failed");
        } finally {
            setScanning(false);
        }
    }

    async function purgeOrphans() {
        setPurging(true);
        try {
            const { data } = await api.post("/self-test/purge-orphans");
            toast.success(
                `Purged ${data.orphan_accounts_deleted} orphan accounts + `
                + `${data.orphan_positions_deleted} orphan positions`
            );
            setConfirmPurge(false);
            // Refresh the scan so the admin sees the new counts.
            await runScan();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Purge failed");
        } finally {
            setPurging(false);
        }
    }

    const orphanCount =
        (report?.counts_by_kind?.orphan_account || 0)
        + (report?.counts_by_kind?.orphan_position || 0);

    const severityRank = { high: 0, medium: 1, low: 2 };
    const { sorted: sortedAnomalies, sortKey, direction, requestSort } = useSortableData(report?.anomalies, {
        accessors: {
            sev: (a) => severityRank[a.severity] ?? 3,
            kind: (a) => a.kind || "",
            subject: (a) => a.email || a.user_id || "",
            detail: (a) => a.detail || "",
        },
    });

    return (
        <div
            className="border border-border bg-card rounded p-4"
            data-testid="selftest-card"
        >
            <div className="flex items-start justify-between gap-2 mb-3">
                <div>
                    <div className="flex items-center gap-2 mb-1">
                        <ShieldCheck className="w-4 h-4 text-primary" />
                        <div className="font-semibold text-sm">Consistency self-test</div>
                    </div>
                    <div className="text-[11px] text-muted-foreground">
                        Read-only scan for validator drift, stuck-frozen users, orphan
                        rows and other silent data issues. Zero side-effects.
                    </div>
                </div>
                <Button
                    onClick={runScan}
                    disabled={scanning}
                    size="sm"
                    data-testid="selftest-run"
                >
                    {scanning
                        ? <Loader2 className="w-3 h-3 animate-spin mr-1.5" />
                        : <RefreshCw className="w-3 h-3 mr-1.5" />}
                    Run scan
                </Button>
            </div>

            {report && (
                <>
                    <div className="flex flex-wrap items-center gap-2 mb-3 pb-3 border-b border-border">
                        <SeverityPill level="high"   count={report.by_severity?.high   || 0} />
                        <SeverityPill level="medium" count={report.by_severity?.medium || 0} />
                        <SeverityPill level="low"    count={report.by_severity?.low    || 0} />
                        <span className="text-[10px] text-muted-foreground mono ml-auto">
                            took {report.took_ms} ms · {report.total} anomalies
                        </span>
                    </div>

                    {/* Zero high + medium == platform-wide OK. */}
                    {!(report.by_severity?.high || report.by_severity?.medium) && (
                        <div className="mb-3 border border-success/40 bg-success/10 rounded p-2 text-xs flex items-center gap-2 text-success">
                            <CheckCircle2 className="w-4 h-4" />
                            <span>No high/medium drift detected — save-side and read-side validators are in sync.</span>
                        </div>
                    )}

                    {orphanCount > 0 && (
                        <div className="mb-3 border border-amber-500/40 bg-amber-500/5 rounded p-2 text-xs flex flex-wrap items-center gap-2">
                            <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
                            <span className="flex-1 min-w-[220px]">
                                {orphanCount} orphan row{orphanCount === 1 ? "" : "s"} detected
                                (accounts / positions whose owning user was hard-deleted).
                                Safe to purge — soft-deleted users are excluded.
                            </span>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setConfirmPurge(true)}
                                disabled={purging}
                                data-testid="selftest-purge-orphans"
                                className="text-amber-700 border-amber-500/40 hover:bg-amber-500 hover:text-white"
                            >
                                <Trash2 className="w-3 h-3 mr-1.5" />
                                Purge orphans
                            </Button>
                        </div>
                    )}

                    {report.total > 0 && (
                        <div className="border border-border rounded overflow-hidden max-h-[340px] overflow-y-auto">
                            <table className="w-full text-xs">
                                <thead className="bg-secondary/40 text-muted-foreground sticky top-0 z-10">
                                    <tr>
                                        <SortableTh label="Sev" sortKey="sev" currentKey={sortKey} direction={direction} onSort={requestSort} className="px-3 py-2" />
                                        <SortableTh label="Kind" sortKey="kind" currentKey={sortKey} direction={direction} onSort={requestSort} className="px-3 py-2" />
                                        <SortableTh label="Subject" sortKey="subject" currentKey={sortKey} direction={direction} onSort={requestSort} className="px-3 py-2" />
                                        <SortableTh label="Detail" sortKey="detail" currentKey={sortKey} direction={direction} onSort={requestSort} className="px-3 py-2" />
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedAnomalies.slice(0, 200).map((a, i) => (
                                        <tr key={`${a.kind}-${a.user_id || a.email || i}`} className="border-t border-border" data-testid={`selftest-row-${i}`}>
                                            <td className="px-3 py-2">
                                                <SeverityPill level={a.severity} count="" />
                                            </td>
                                            <td className="px-3 py-2 mono font-semibold">{a.kind}</td>
                                            <td className="px-3 py-2 mono text-[10px]">{a.email || a.user_id}</td>
                                            <td className="px-3 py-2 text-[10px] text-muted-foreground">{a.detail}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            {report.total > 200 && (
                                <div className="text-[10px] text-muted-foreground text-center py-2 border-t border-border">
                                    Showing first 200 of {report.total} · rest available via
                                    <span className="mono"> GET /api/self-test/consistency</span>.
                                </div>
                            )}
                        </div>
                    )}
                </>
            )}

            {!report && !scanning && (
                <div className="text-[11px] text-muted-foreground italic py-4 text-center">
                    Click <strong>Run scan</strong> above to probe the platform for drift.
                </div>
            )}

            <AlertDialog open={confirmPurge} onOpenChange={setConfirmPurge}>
                <AlertDialogContent data-testid="selftest-purge-confirm">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Purge orphan accounts + positions?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This will permanently delete <strong>{orphanCount} orphan document
                            {orphanCount === 1 ? "" : "s"}</strong> whose owning user no longer
                            exists in the <span className="mono">users</span> collection.
                            Soft-deleted users (status = deleted) are NOT touched. The action is
                            audit-logged to the <span className="mono">orphan_purges</span> collection.
                            Cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="selftest-purge-cancel">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            data-testid="selftest-purge-confirm-btn"
                            onClick={purgeOrphans}
                            disabled={purging}
                            className="bg-amber-500 text-white hover:bg-amber-600"
                        >
                            {purging ? "Purging…" : "Purge now"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
